import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { EmailBody } from "@/components/EmailBody";
import { TicketReplyEditor } from "@/components/TicketReplyEditor";
import { TicketAttachment } from "@/components/TicketAttachment";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Send,
  StickyNote,
  Trash2,
  Loader2,
  MessageSquare,
  Reply,
  ExternalLink,
  Search,
  CheckCircle2,
  Sparkles,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";

const COLOR_TO_CLASS = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  lime: "bg-lime-50 text-lime-700 border-lime-200",
};

function ageOf(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

function fmtMinutes(m) {
  const n = Number(m) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

// Strip tags from an HTML string for the plain-text search-friendly fallback
// stored alongside the HTML body. Doesn't need to be perfect — the HTML is
// the source of truth; this just helps notification previews and search.
function _htmlToText(html) {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = html;
    return (el.innerText || el.textContent || "").trim();
  }
  return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}


export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canDeleteTickets = user?.role === "admin" || user?.role === "support";

  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Form state (mirrors ticket but is editable)
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fixingInline, setFixingInline] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const inFlightAutoSaveRef = useRef(false);

  const reprocessInline = async () => {
    setFixingInline(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/reprocess-inline`);
      if (r.data?.updated) {
        toast.success(`Restored ${r.data.inline_images} inline image(s)`);
      } else {
        console.log("reprocess-inline diagnostics:", r.data);
        toast.info(
          `No inline images found — saw ${r.data?.attachments_seen ?? 0} attachment(s), HTML body: ${r.data?.html_body_present ? "yes" : "no"}`
        );
      }
      await loadAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fix inline images");
    } finally {
      setFixingInline(false);
    }
  };

  // Customer search
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  // Account contacts for requester picker
  const [accountContacts, setAccountContacts] = useState([]);

  // Compose (draft) state
  const [composeTab, setComposeTab] = useState("reply");
  const [publicDraft, setPublicDraft] = useState("");
  const [privateDraft, setPrivateDraft] = useState("");
  const [publicTime, setPublicTime] = useState("");
  const [privateTime, setPrivateTime] = useState("");
  const [closeOnSend, setCloseOnSend] = useState(false);
  const [suppressCloseEmail, setSuppressCloseEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const draftDebounceRef = useRef(null);

  // AI reply assist state (public reply only)
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // { mode, html, notes }

  const threadRef = useRef(null);

  // ---------- Data loading ----------
  const loadAll = async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const [tk, ev, st, ac, vd, pr, sts, pri, dr] = await Promise.all([
        api.get(`/tickets/${ticketId}`),
        api.get(`/tickets/${ticketId}/events`).catch(() => ({ data: [] })),
        api.get("/staff").catch(() => ({ data: [] })),
        api.get("/accounts").catch(() => ({ data: [] })),
        api.get("/vendors").catch(() => ({ data: [] })),
        api.get("/stax-products").catch(() => ({ data: [] })),
        api.get("/config/ticket-statuses").catch(() => ({ data: [] })),
        api.get("/config/ticket-priorities").catch(() => ({ data: [] })),
        api.get(`/tickets/${ticketId}/draft`).catch(() => ({ data: { public: "", private: "" } })),
      ]);
      // If we were addressed by ticket number (or any non-canonical id), the backend
      // still resolves the ticket — redirect to its canonical id so events/draft load correctly.
      if (tk.data?.id && String(tk.data.id) !== String(ticketId)) {
        navigate(`/app/tickets/${tk.data.id}`, { replace: true });
        return;
      }
      setTicket(tk.data);
      setEvents(ev.data);
      setStaff(st.data);
      setAccounts(ac.data);
      setVendors(vd.data);
      setProducts(pr.data);
      setStatuses(sts.data);
      setPriorities(pri.data);
      if (initial) {
        setForm({
          subject: tk.data.subject || "",
          description: tk.data.description || "",
          status: tk.data.status || "",
          priority: tk.data.priority || "",
          category: tk.data.category || "Support",
          owner_user_id: tk.data.owner_user_id || "",
          account_id: tk.data.account_id || "",
          vendor_id: tk.data.vendor_id || "",
          product_id: tk.data.product_id || "",
          vendor_ticket_number: tk.data.vendor_ticket_number || "",
          requester_contact_id: tk.data.requester_contact_id || "",
          resolution_notes: tk.data.resolution_notes || "",
        });
        setDirty(false);
        setPublicDraft(dr.data.public || "");
        setPrivateDraft(dr.data.private || "");
      }
      // Auto-scroll thread to bottom
      setTimeout(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Failed to load ticket");
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    loadAll(true);
    // eslint-disable-next-line
  }, [ticketId]);

  // Load contacts for the current account whenever it changes
  useEffect(() => {
    let cancelled = false;
    if (!form.account_id) { setAccountContacts([]); return; }
    (async () => {
      try {
        const r = await api.get(`/accounts/${form.account_id}/contacts`);
        if (!cancelled) setAccountContacts(r.data || []);
      } catch (e) {
        if (!cancelled) setAccountContacts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.account_id]);

  // ---------- Derived lookups ----------
  const statusColors = useMemo(() => Object.fromEntries(statuses.map((s) => [s.name, s.color || "slate"])), [statuses]);
  const priorityColors = useMemo(() => Object.fromEntries(priorities.map((p) => [p.name, p.color || "slate"])), [priorities]);
  const account = accounts.find((a) => a.id === form.account_id);
  const filteredAccounts = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return accounts.slice(0, 60);
    return accounts.filter((a) => a.name?.toLowerCase().includes(q)).slice(0, 60);
  }, [accounts, customerSearch]);

  // ---------- Handlers ----------
  // Fields that trip the "will close on save" prompt — the manual Save
  // button lives on for these so the suppress-close-email checkbox has a
  // chance to appear.
  const isCloseTransition = (nextStatus) => {
    if (!nextStatus || nextStatus === ticket.status) return false;
    const nextIsClosed = !!statuses.find((s) => s.name === nextStatus && s.is_closed);
    const prevIsClosed = !!statuses.find((s) => s.name === ticket.status && s.is_closed);
    return nextIsClosed && !prevIsClosed;
  };

  // Autosave — fires 800 ms after the last field change so rapid typing on
  // Description / Resolution / vendor-ticket-number doesn't fan out one
  // PATCH per keystroke. Dropdown changes (status / owner / priority /
  // category / vendor / product / account / contact) fire quickly because
  // the debounce resets each change.
  const scheduleAutoSave = (nextForm) => {
    // If the pending change is closing the ticket, skip autosave so the
    // user can tick "Don't email customer on close" before confirming.
    if (isCloseTransition(nextForm.status)) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => doAutoSave(nextForm), 800);
  };

  const doAutoSave = async (snapshot) => {
    if (inFlightAutoSaveRef.current) {
      // Another autosave in flight — reschedule to the next tick so the
      // latest keystroke isn't lost.
      autoSaveTimerRef.current = setTimeout(() => doAutoSave(snapshot), 400);
      return;
    }
    inFlightAutoSaveRef.current = true;
    setAutoSaving(true);
    try {
      const payload = { ...snapshot };
      if (payload.requester_contact_id && !accountContacts.find((c) => c.id === payload.requester_contact_id)) {
        payload.requester_contact_id = null;
      }
      ["owner_user_id", "vendor_id", "product_id", "vendor_ticket_number", "description", "resolution_notes", "requester_contact_id"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.patch(`/tickets/${ticketId}`, payload);
      setDirty(false);
      setLastSavedAt(new Date());
    } catch (e) {
      // Don't nag the user with error toasts on every keystroke — surface
      // only when the manual Save button is used. The indicator stays on
      // "Not saved" so they know something is wrong.
      console.warn("Ticket autosave failed:", e?.response?.data?.detail || e?.message);
    } finally {
      inFlightAutoSaveRef.current = false;
      setAutoSaving(false);
    }
  };

  const setField = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      setDirty(true);
      scheduleAutoSave(next);
      return next;
    });
  };

  // Flush any pending autosave before navigating away.
  useEffect(() => () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // If the requester contact no longer belongs to the selected account, clear it.
      if (payload.requester_contact_id && !accountContacts.find((c) => c.id === payload.requester_contact_id)) {
        payload.requester_contact_id = null;
      }
      // Empty string → null (backend expects null for cleared refs)
      ["owner_user_id", "vendor_id", "product_id", "vendor_ticket_number", "description", "resolution_notes", "requester_contact_id"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (willCloseOnSave && suppressCloseEmail) payload.suppress_close_notification = true;
      await api.patch(`/tickets/${ticketId}`, payload);
      toast.success(willCloseOnSave && suppressCloseEmail ? "Ticket closed — no email sent to the customer" : "Ticket saved");
      setDirty(false);
      setSuppressCloseEmail(false);
      await loadAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async () => {
    if (!window.confirm(`Delete ticket #${ticket.ticket_number || ticket.id}? It will be moved to the Admin → Ticket recycle bin (nothing is truly deleted) and can be restored at any time.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success("Ticket deleted");
      navigate("/app/tickets");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  // Debounced draft persistence — stores the raw HTML so pasted screenshots
  // survive a reload. The textual preview on the Draft column keeps using
  // the plain-text extract (toText below) so draft lists stay clean.
  const persistDraft = (kind, value) => {
    if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(async () => {
      setSavingDraft(true);
      try {
        await api.put(`/tickets/${ticketId}/draft`, { [kind]: value });
      } catch (e) { /* silent */ }
      finally { setSavingDraft(false); }
    }, 700);
  };

  const onPublicDraftChange = (v) => { setPublicDraft(v); persistDraft("public", v); };
  const onPrivateDraftChange = (v) => { setPrivateDraft(v); persistDraft("private", v); };

  // AI reply assist — asks Claude to draft a reply (empty editor) or improve
  // whatever the engineer has already typed. The suggestion appears in a
  // card below the editor; the engineer explicitly accepts or dismisses it.
  const requestAiSuggestion = async () => {
    const currentHtml = publicEditorRef.current?.getHTML?.() ?? publicDraft ?? "";
    const cleaned = currentHtml.trim() === "<br>" ? "" : currentHtml;
    setAiBusy(true);
    try {
      const r = await api.post(`/tickets/${ticketId}/ai-reply-suggest`, { draft_html: cleaned });
      setAiSuggestion(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't get an AI suggestion");
    } finally { setAiBusy(false); }
  };
  const acceptAiSuggestion = () => {
    if (!aiSuggestion?.html) return;
    setPublicDraft(aiSuggestion.html);
    persistDraft("public", aiSuggestion.html);
    setAiSuggestion(null);
    toast.success("AI suggestion loaded into the reply");
  };

  // Pull the current plain-text + HTML off the rich editors. We keep both
  // because the backend stores plain text for search/snippets and HTML for
  // thread rendering + outbound email with inline images.
  const publicEditorRef = useRef(null);
  const privateEditorRef = useRef(null);
  const extractDraft = (isPrivate) => {
    const ref = isPrivate ? privateEditorRef.current : publicEditorRef.current;
    const html = ref?.getHTML?.() || (isPrivate ? privateDraft : publicDraft) || "";
    const text = ref?.getText?.() || _htmlToText(html);
    return { html: html.trim() === "<br>" ? "" : html, text };
  };

  const sendCompose = async () => {
    const isPrivate = composeTab === "note";
    const { html: bodyHtml, text: body } = extractDraft(isPrivate);
    if (!body) return toast.error(isPrivate ? "Note body required" : "Reply body required");
    // Guard against sending while a pasted image upload is still in flight —
    // otherwise the customer email would reference a URL that doesn't exist yet.
    const ref = isPrivate ? privateEditorRef.current : publicEditorRef.current;
    if (ref?.isUploading?.()) {
      return toast.error("Waiting for pasted image upload to finish — try again in a moment.");
    }
    const timeStr = (isPrivate ? privateTime : publicTime).toString().trim();
    const timeMin = Number(timeStr);
    if (!timeStr || !Number.isFinite(timeMin) || timeMin < 1) {
      return toast.error("Time spent (minutes) is required — enter at least 1 minute.");
    }
    if (timeMin > 1440) {
      return toast.error("Time spent cannot exceed 1440 min (24h) per update.");
    }
    // Resolve the actual "Closed" status name from the configured statuses.
    // Prefer the last one flagged is_closed=true (typically "Closed" after "Resolved"),
    // fall back to a status literally named "Closed", then to the string "Closed".
    const closedStatuses = statuses.filter((s) => s.is_closed);
    const closedStatusName =
      closedStatuses.find((s) => (s.name || "").toLowerCase() === "closed")?.name ||
      closedStatuses[closedStatuses.length - 1]?.name ||
      "Closed";

    setSending(true);
    try {
      await api.post(`/tickets/${ticketId}/events`, {
        kind: isPrivate ? "note" : "reply",
        body,
        body_html: bodyHtml || null,
        is_private: isPrivate,
        time_spent_minutes: Math.round(timeMin),
      });
      // If the engineer ticked "Close ticket on send", move the ticket to the
      // closed status. For PUBLIC replies, the reply body doubles as the
      // resolution notes (customer-visible in the closure email). For PRIVATE
      // notes, we deliberately do NOT copy the note into resolution_notes and
      // flag the closure as private-resolution so any customer closure email
      // suppresses the resolution_notes field entirely (private notes must
      // never reach the customer). Non-fatal if the PATCH fails — the update
      // itself is already recorded.
      let closedNow = false;
      if (closeOnSend) {
        try {
          const patch = isPrivate
            ? { status: closedStatusName, resolution_is_private: true }
            : { status: closedStatusName, resolution_notes: body, resolution_is_private: false };
          if (suppressCloseEmail) patch.suppress_close_notification = true;
          await api.patch(`/tickets/${ticketId}`, patch);
          closedNow = true;
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Update sent, but failed to close the ticket");
        }
      }
      if (isPrivate) {
        setPrivateDraft(""); setPrivateTime("");
        privateEditorRef.current?.clear?.();
      } else {
        setPublicDraft(""); setPublicTime("");
        publicEditorRef.current?.clear?.();
      }
      setCloseOnSend(false);
      setSuppressCloseEmail(false);
      if (closedNow) {
        toast.success(`Ticket closed · ${Math.round(timeMin)} min booked · resolution recorded`);
      } else {
        toast.success(`${isPrivate ? "Private note added" : "Reply sent"} · ${Math.round(timeMin)} min booked`);
      }
      await loadAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return <div className="p-12 text-center text-slate-400 text-sm">Loading ticket…</div>;
  }
  if (notFound || !ticket) {
    return (
      <Card className="p-12 text-center border-slate-200">
        <div className="font-heading text-lg text-slate-700">Ticket not found</div>
        <Button asChild variant="ghost" className="mt-4">
          <Link to="/app/tickets">← Back to tickets</Link>
        </Button>
      </Card>
    );
  }

  const tNum = ticket.ticket_number ? `#${ticket.ticket_number}` : ticket.id;
  const statusPillColor = COLOR_TO_CLASS[statusColors[ticket.status] || "slate"];
  const priorityPillColor = COLOR_TO_CLASS[priorityColors[ticket.priority] || "slate"];
  // True when the pending Save will transition the ticket from open → closed.
  // Used to conditionally show the "don't email customer" suppression toggle
  // next to the Save button.
  const willCloseOnSave = (() => {
    if (!dirty || !form.status || form.status === ticket.status) return false;
    const nextIsClosed = !!statuses.find((s) => s.name === form.status && s.is_closed);
    const prevIsClosed = !!statuses.find((s) => s.name === ticket.status && s.is_closed);
    return nextIsClosed && !prevIsClosed;
  })();

  return (
    <div className="space-y-5" data-testid="ticket-detail-page">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/tickets")}
            data-testid="back-to-tickets"
            className="text-slate-500"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to tickets
          </Button>
          <div className="text-xs font-mono text-slate-500">
            {tNum} · Opened {fmtDateTime(ticket.created_at)} · Age {ageOf(ticket.created_at)} · <span className="text-blue-700 font-semibold">Time booked: {fmtMinutes(ticket.time_spent_total_minutes)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusPillColor}`} data-testid="detail-status-pill">
            {ticket.status}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${priorityPillColor}`}>
            {ticket.priority}
          </span>
          {willCloseOnSave && (
            <label
              className="flex items-center gap-1.5 px-2 py-1 rounded border border-amber-200 bg-amber-50 text-[11px] text-amber-900 cursor-pointer"
              data-testid="detail-suppress-close-email-wrapper"
              title="Close without emailing the marked contact — useful for SOC-generated tickets resolved in house."
            >
              <Checkbox
                checked={suppressCloseEmail}
                onCheckedChange={(v) => setSuppressCloseEmail(!!v)}
                data-testid="detail-suppress-close-email-checkbox"
              />
              <span>Don't email customer on close</span>
            </label>
          )}
          {willCloseOnSave ? (
            <Button onClick={save} disabled={!dirty || saving} data-testid="save-ticket-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Close ticket
            </Button>
          ) : (
            <span
              className="text-xs text-slate-500 flex items-center gap-1.5 min-w-[90px]"
              data-testid="autosave-indicator"
              title={lastSavedAt ? `Last saved ${lastSavedAt.toLocaleTimeString()}` : "Changes save automatically"}
            >
              {autoSaving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
                </>
              ) : dirty ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-amber-400" /> Unsaved
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5 text-emerald-600" /> Saved
                </>
              )}
            </span>
          )}
          {canDeleteTickets && (
            <Button
              variant="ghost"
              size="icon"
              onClick={deleteTicket}
              disabled={deleting}
              data-testid="delete-ticket-btn"
              className="text-slate-500 hover:text-red-600 hover:bg-red-50"
              aria-label="Delete ticket"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Subject as page title */}
      <div>
        <Input
          value={form.subject}
          onChange={(e) => setField("subject", e.target.value)}
          data-testid="detail-subject"
          className="text-2xl font-heading font-bold h-auto py-2 border-transparent hover:border-slate-200 focus-visible:border-blue-500 shadow-none px-2 -mx-2"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT — details form */}
        <Card className="lg:col-span-1 border-slate-200 p-5 space-y-4" data-testid="detail-form">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statuses.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Assigned owner</Label>
            <Select
              value={form.owner_user_id || "__none__"}
              onValueChange={(v) => setField("owner_user_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="detail-owner-select">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {staff.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name}{u.job_title ? ` · ${u.job_title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setField("priority", v)}>
              <SelectTrigger data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Category</Label>
            <Select value={form.category || "Support"} onValueChange={(v) => setField("category", v)}>
              <SelectTrigger data-testid="detail-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Support">Support</SelectItem>
                <SelectItem value="Service">Service</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-slate-400">Service = Managed Services work; excluded from support KPIs, tracked as capacity.</p>
          </div>

          {/* Customer picker */}
          <div>
            <Label className="text-xs">Customer</Label>
            {customerPickerOpen ? (
              <div className="space-y-1.5">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    autoFocus
                    placeholder="Search accounts…"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-8 h-9"
                    data-testid="detail-customer-search"
                  />
                </div>
                <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white shadow-sm">
                  {filteredAccounts.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400">No matches</div>
                  ) : (
                    filteredAccounts.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => {
                          setField("account_id", a.id);
                          setCustomerPickerOpen(false);
                          setCustomerSearch("");
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-b-0 border-slate-50"
                        data-testid={`detail-customer-option-${a.id}`}
                      >
                        {a.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCustomerPickerOpen(true)}
                  data-testid="detail-customer-button"
                  className="flex-1 text-left border border-slate-200 rounded-md px-3 py-2 text-sm hover:border-blue-400 transition-colors bg-white"
                >
                  {account?.name || ticket.account_name || <span className="text-slate-400">Pick customer…</span>}
                </button>
                {account && (
                  <Link
                    to={`/app/accounts/${account.id}`}
                    data-testid="detail-open-account"
                    className="text-slate-400 hover:text-blue-600"
                    title="Open account"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Requester contact — narrows updates to a specific person on the account */}
          <div>
            <Label className="text-xs">Requester contact</Label>
            <Select
              value={form.requester_contact_id || "__none__"}
              onValueChange={(v) => setField("requester_contact_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="detail-requester-select">
                <SelectValue placeholder={accountContacts.length ? "Pick a contact" : "No contacts on this account"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {accountContacts.map((c) => {
                  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Unknown";
                  return (
                    <SelectItem key={c.id} value={c.id} data-testid={`detail-requester-option-${c.id}`}>
                      {name}{c.email ? ` · ${c.email}` : ""}{c.title ? ` · ${c.title}` : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {form.requester_contact_id && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="detail-requester-hint">
                Public replies will go to this contact first.
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Vendor</Label>
            <Select
              value={form.vendor_id || "__none__"}
              onValueChange={(v) => setField("vendor_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="detail-vendor-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Product</Label>
            <Select
              value={form.product_id || "__none__"}
              onValueChange={(v) => setField("product_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="detail-product-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Vendor ticket number</Label>
            <Input
              value={form.vendor_ticket_number}
              onChange={(e) => setField("vendor_ticket_number", e.target.value)}
              placeholder="Reference from the vendor"
              data-testid="detail-vendor-number"
            />
          </div>

          <div>
            <Label className="text-xs">Fault description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={5}
              placeholder="Describe the fault or issue"
              data-testid="detail-description"
              spellCheck={true}
              lang="en"
              autoCorrect="on"
              autoCapitalize="sentences"
            />
          </div>

          <div>
            <Label className="text-xs">Resolution notes</Label>
            <Textarea
              value={form.resolution_notes}
              onChange={(e) => setField("resolution_notes", e.target.value)}
              rows={3}
              placeholder="Fill in when resolving"
              data-testid="detail-resolution-notes"
              spellCheck={true}
              lang="en"
              autoCorrect="on"
              autoCapitalize="sentences"
            />
          </div>

          {(() => {
            const atts = (events || []).filter((e) => (e.kind === "attachment" || e.kind === "inline_image") && e.meta?.id);
            if (atts.length === 0) return null;
            return (
              <div className="pt-1" data-testid="detail-attachments">
                <Label className="text-xs flex items-center gap-1.5">
                  Attachments
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{atts.length}</span>
                </Label>
                <div className="mt-1.5 space-y-1.5">
                  {atts.map((ev) => (
                    <TicketAttachment key={ev.id} ticketId={ticket.id} meta={ev.meta} createdAt={ev.created_at} fmt={fmtDateTime} />
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="pt-2 text-[11px] font-mono text-slate-400 border-t border-slate-100">
            Created {fmtDateTime(ticket.created_at)} · Updated {fmtDateTime(ticket.updated_at)}
          </div>
        </Card>

        {/* RIGHT — thread + compose */}
        <div className="lg:col-span-2 space-y-4">
          {/* Thread */}
          <Card className="border-slate-200 flex flex-col" data-testid="detail-thread">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-heading font-bold text-slate-900">
                <MessageSquare className="h-4 w-4 text-blue-600" />
                Conversation
                <span className="text-[11px] font-mono text-slate-400 font-normal">({events.length})</span>
              </div>
            </div>
            <div ref={threadRef} className="p-4 space-y-3 bg-slate-50/40 max-h-[520px] overflow-y-auto">
              {ticket.description && (
                <div className="bg-white border border-slate-200 rounded-lg p-3.5">
                  <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono mb-1.5">
                    <span>Fault description</span>
                    <div className="flex items-center gap-2">
                      {isAdmin && ticket.source_message_id && !ticket.description_html && (
                        <button
                          type="button"
                          data-testid="fix-inline-images-btn"
                          onClick={reprocessInline}
                          disabled={fixingInline}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 font-sans"
                        >
                          {fixingInline ? "Fixing…" : "Fix inline images"}
                        </button>
                      )}
                      <span>{fmtDateTime(ticket.created_at)}</span>
                    </div>
                  </div>
                  {ticket.description_html
                    ? <EmailBody html={ticket.description_html} />
                    : <div className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</div>}
                </div>
              )}
              {events.map((ev) => {
                if (ev.kind === "inline_image") return null;
                if (ev.kind === "attachment") {
                  return (
                    <TicketAttachment key={ev.id} ticketId={ticket.id} meta={ev.meta} createdAt={ev.created_at} fmt={fmtDateTime} />
                  );
                }
                const isNote = ev.kind === "note" || ev.is_private;
                const isSystem = ev.kind === "system" || ev.kind === "status_change" || ev.kind === "assignment";
                if (isSystem) {
                  return (
                    <div key={ev.id} className="text-[11px] font-mono text-slate-400 text-center py-1">
                      {ev.body} · {fmtDateTime(ev.created_at)}
                    </div>
                  );
                }
                return (
                  <div
                    key={ev.id}
                    data-testid={`thread-event-${ev.id}`}
                    className={`rounded-lg p-3.5 border ${isNote ? "bg-amber-50/60 border-amber-200" : "bg-white border-slate-200"}`}
                  >
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                        {isNote ? <StickyNote className="h-3 w-3 text-amber-600" /> : <Reply className="h-3 w-3 text-blue-600" />}
                        {ev.author_name || "Unknown"}
                        {isNote && <span className="text-amber-700 font-mono ml-1">PRIVATE NOTE</span>}
                        {ev.time_spent_minutes ? (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100/70 text-blue-700 font-mono">
                            {fmtMinutes(ev.time_spent_minutes)}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-slate-400 font-mono">{fmtDateTime(ev.created_at)}</span>
                    </div>
                    {ev.body_html
                      ? <EmailBody html={ev.body_html} />
                      : <div className="text-sm text-slate-700 whitespace-pre-wrap">{ev.body}</div>}
                  </div>
                );
              })}
              {events.length === 0 && !ticket.description && (
                <div className="text-center text-xs text-slate-400 py-6">No updates yet</div>
              )}
            </div>
          </Card>

          {/* Compose (Reply / Note tabs) */}
          <Card className="border-slate-200" data-testid="detail-compose">
            <Tabs value={composeTab} onValueChange={setComposeTab}>
              <div className="border-b border-slate-100 px-4 pt-3 flex items-center justify-between">
                <TabsList className="bg-transparent p-0 gap-1">
                  <TabsTrigger
                    value="reply"
                    data-testid="compose-tab-reply"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-3 py-2 text-sm"
                  >
                    <Reply className="h-3.5 w-3.5 mr-1.5" /> Public reply
                  </TabsTrigger>
                  <TabsTrigger
                    value="note"
                    data-testid="compose-tab-note"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:text-amber-700 data-[state=active]:shadow-none px-3 py-2 text-sm"
                  >
                    <StickyNote className="h-3.5 w-3.5 mr-1.5" /> Private note
                  </TabsTrigger>
                </TabsList>
                <div className="text-[10px] font-mono text-slate-400 pb-2">
                  {savingDraft ? "saving draft…" : "draft auto-saved"}
                </div>
              </div>

              <TabsContent value="reply" className="p-4 space-y-3 m-0">
                <TicketReplyEditor
                  ref={publicEditorRef}
                  ticketId={ticketId}
                  value={publicDraft}
                  onChange={({ html }) => setPublicDraft(html)}
                  onDraftChange={(_text, html) => persistDraft("public", html)}
                  placeholder="Type a reply to the customer — paste screenshots inline. Sent via email when you click Send."
                  dataTestId="compose-public-body"
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={requestAiSuggestion}
                    disabled={aiBusy}
                    data-testid="ai-reply-assist-btn"
                    className="h-8 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                    title="Reads the thread so far and either drafts a reply (if empty) or suggests improvements to what you've typed"
                  >
                    {aiBusy ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {publicDraft?.trim() ? "Improve with AI" : "Draft with AI"}
                  </Button>
                  <span className="text-[11px] text-slate-400">Powered by Claude · reads the full ticket thread</span>
                </div>
                {aiSuggestion && (
                  <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid="ai-reply-suggestion">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-violet-800 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5" />
                        {aiSuggestion.mode === "improve" ? "Suggested improvements" : "Suggested draft"}
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSuggestion(null)}
                        className="text-slate-400 hover:text-slate-700"
                        aria-label="Dismiss AI suggestion"
                        data-testid="ai-reply-dismiss-btn"
                      >
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div
                      className="prose prose-sm max-w-none text-slate-800 bg-white border border-slate-200 rounded p-3"
                      data-testid="ai-reply-suggestion-body"
                      dangerouslySetInnerHTML={{ __html: aiSuggestion.html }}
                    />
                    {!!(aiSuggestion.notes && aiSuggestion.notes.length) && (
                      <ul className="text-[11px] text-slate-600 list-disc pl-5 space-y-0.5">
                        {aiSuggestion.notes.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={acceptAiSuggestion}
                        data-testid="ai-reply-accept-btn"
                        className="h-7 bg-violet-600 hover:bg-violet-700 text-white"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Use this reply
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={requestAiSuggestion}
                        disabled={aiBusy}
                        data-testid="ai-reply-regenerate-btn"
                        className="h-7 text-slate-600"
                      >
                        {aiBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                        Regenerate
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-end gap-3 flex-wrap">
                  <div className="text-[11px] text-slate-500 max-w-[50%] space-y-2">
                    <div>
                      Recipients: {ticket.requester_contact_name ? (
                        <>
                          <span className="text-slate-700 font-medium">{ticket.requester_contact_name}</span> ({ticket.requester_contact_email || "no email on contact"})
                        </>
                      ) : (
                        "the person who raised the ticket, or a contact on the account"
                      )}. Sent from the Microsoft 365 support mailbox (support@xentra.co.uk). <span className="text-slate-400">Pasted screenshots are embedded inline — no attachments.</span>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer group" data-testid="compose-public-close-wrapper">
                      <Checkbox
                        checked={closeOnSend}
                        onCheckedChange={(v) => setCloseOnSend(!!v)}
                        data-testid="compose-public-close-checkbox"
                        className="mt-0.5"
                      />
                      <span className="text-[11px] text-slate-600 group-hover:text-slate-900 transition-colors leading-snug">
                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> Close ticket on send
                        </span>
                        {" — "}the reply text will be saved as the resolution notes and the ticket status set to <span className="font-mono">Closed</span>.
                      </span>
                    </label>
                    {closeOnSend && (
                      <label className="ml-6 flex items-start gap-2 cursor-pointer group" data-testid="compose-public-suppress-wrapper">
                        <Checkbox
                          checked={suppressCloseEmail}
                          onCheckedChange={(v) => setSuppressCloseEmail(!!v)}
                          data-testid="compose-public-suppress-checkbox"
                          className="mt-0.5"
                        />
                        <span className="text-[11px] text-amber-800 group-hover:text-amber-900 transition-colors leading-snug">
                          Don't email the customer contact — closes silently. Useful when a SOC-raised ticket is resolved in house.
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Time (min) *</Label>
                      <Input
                        type="number"
                        min={1}
                        max={1440}
                        step={1}
                        value={publicTime}
                        onChange={(e) => setPublicTime(e.target.value)}
                        placeholder="30"
                        className="h-9 w-24"
                        data-testid="compose-public-time"
                        required
                      />
                    </div>
                    <Button
                      onClick={sendCompose}
                      disabled={sending || !publicDraft.trim() || !publicTime}
                      data-testid="compose-send-reply"
                      className={closeOnSend ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : (closeOnSend ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />)}
                      {closeOnSend ? "Send & close" : "Send reply"}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="note" className="p-4 space-y-3 m-0">
                <TicketReplyEditor
                  ref={privateEditorRef}
                  ticketId={ticketId}
                  value={privateDraft}
                  onChange={({ html }) => setPrivateDraft(html)}
                  onDraftChange={(_text, html) => persistDraft("private", html)}
                  placeholder="Internal-only note. Only Xentra staff see this — not emailed to the customer. Paste screenshots inline."
                  dataTestId="compose-private-body"
                />
                <div className="flex justify-between items-end gap-3 flex-wrap">
                  <div className="text-[11px] text-slate-500 max-w-[50%] space-y-2">
                    <div>Visible only to Xentra staff on this ticket.</div>
                    <label className="flex items-start gap-2 cursor-pointer group" data-testid="compose-private-close-wrapper">
                      <Checkbox
                        checked={closeOnSend}
                        onCheckedChange={(v) => setCloseOnSend(!!v)}
                        data-testid="compose-private-close-checkbox"
                        className="mt-0.5"
                      />
                      <span className="text-[11px] text-slate-600 group-hover:text-slate-900 transition-colors leading-snug">
                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> Close ticket on save
                        </span>
                        {" — "}the ticket status is set to <span className="font-mono">Closed</span>. The private note stays internal and is <span className="font-semibold">NOT</span> included in the customer closure email.
                      </span>
                    </label>
                    {closeOnSend && (
                      <label className="ml-6 flex items-start gap-2 cursor-pointer group" data-testid="compose-private-suppress-wrapper">
                        <Checkbox
                          checked={suppressCloseEmail}
                          onCheckedChange={(v) => setSuppressCloseEmail(!!v)}
                          data-testid="compose-private-suppress-checkbox"
                          className="mt-0.5"
                        />
                        <span className="text-[11px] text-amber-800 group-hover:text-amber-900 transition-colors leading-snug">
                          Don't email the customer contact — closes silently. Useful when a SOC-raised ticket is resolved in house.
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Time (min) *</Label>
                      <Input
                        type="number"
                        min={1}
                        max={1440}
                        step={1}
                        value={privateTime}
                        onChange={(e) => setPrivateTime(e.target.value)}
                        placeholder="15"
                        className="h-9 w-24"
                        data-testid="compose-private-time"
                        required
                      />
                    </div>
                    <Button
                      onClick={sendCompose}
                      disabled={sending || !privateDraft.trim() || !privateTime}
                      className={closeOnSend ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}
                      data-testid="compose-save-note"
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : (closeOnSend ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> : <StickyNote className="h-3.5 w-3.5 mr-1.5" />)}
                      {closeOnSend ? "Save & close" : "Save note"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {isAdmin && <TicketAuditTrail ticketId={ticket.id} />}
    </div>
  );
}

// ---------------- Audit trail (admin only) ----------------
function TicketAuditTrail({ ticketId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tickets/${ticketId}/audit`);
      setRows(data || []);
    } catch (e) {
      // Silent — non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && ticketId) load();
    // eslint-disable-next-line
  }, [open, ticketId]);

  const actionColor = {
    create: "bg-emerald-50 text-emerald-700 border-emerald-200",
    update: "bg-blue-50 text-blue-700 border-blue-200",
    delete: "bg-rose-50 text-rose-700 border-rose-200",
    restore: "bg-violet-50 text-violet-700 border-violet-200",
    event_add: "bg-slate-50 text-slate-600 border-slate-200",
  };

  return (
    <Card className="mt-4 border-slate-200" data-testid="ticket-audit-trail">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid="audit-toggle"
      >
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Admin only</div>
          <div className="font-heading text-sm font-semibold text-slate-900">Immutable audit trail</div>
        </div>
        <span className="text-xs text-slate-500 font-mono">
          {open ? "hide" : "show"} · {rows.length || "—"} entries
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4">
          {loading ? (
            <div className="text-center text-xs text-slate-400 py-6">
              <Loader2 className="h-4 w-4 mx-auto animate-spin mb-1" /> Loading audit entries…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-6">No audit entries yet.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => (
                <div
                  key={row.id}
                  data-testid={`audit-row-${row.id}`}
                  className="border border-slate-200 rounded-lg p-3 text-xs bg-white"
                >
                  <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${actionColor[row.action] || actionColor.event_add}`}>
                        {row.action}
                      </span>
                      <span className="text-slate-600">{row.actor_name || "System"}</span>
                    </div>
                    <span className="font-mono text-slate-400">{fmtDateTime(row.created_at)}</span>
                  </div>
                  {row.changed_fields && row.changed_fields.length > 0 && (
                    <div className="text-slate-500 text-[11px] mb-1">
                      <span className="font-semibold">Fields:</span>{" "}
                      <span className="font-mono">{row.changed_fields.join(", ")}</span>
                    </div>
                  )}
                  {row.meta && (
                    <details className="text-[11px] text-slate-500 mt-1">
                      <summary className="cursor-pointer">meta</summary>
                      <pre className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-[10px]">{JSON.stringify(row.meta, null, 2)}</pre>
                    </details>
                  )}
                  {row.before && row.after && (
                    <details className="text-[11px] text-slate-500 mt-1">
                      <summary className="cursor-pointer">before / after snapshot</summary>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                        <pre className="bg-rose-50/60 border border-rose-100 rounded p-2 overflow-x-auto text-[10px]">{JSON.stringify(row.before, null, 2)}</pre>
                        <pre className="bg-emerald-50/60 border border-emerald-100 rounded p-2 overflow-x-auto text-[10px]">{JSON.stringify(row.after, null, 2)}</pre>
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
